import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import Provider from "../../../../enum/Provider.enum";

export type ArmeniaAct = {
  id: number;
  office?: number;
  tn_id?: string;
  status?: string;
  version?: number;
  act_date?: string;
  updated_at?: string;
  metadata?: any;
  disputes?: any[];
  signed_files_count?: number;

  signed_file_url?: string;
  dispute?: {
    comment?: string;
    accounts?: string[];
  };
  lang?: "ru" | "en";
};

type Props = {
  acts: ArmeniaAct[];
  isAdmin: boolean;
  onOpenAct: (act: ArmeniaAct, lang: "ru" | "en") => void;
  onOpenDispute: (act: ArmeniaAct) => void;
  onOpenDisputePreview: (act: ArmeniaAct) => void;
  onOpenSignedAct: (act: ArmeniaAct) => void;
  onUploadSignedAct: (act: ArmeniaAct) => void;
  onAgreeAct: (act: ArmeniaAct) => void;
  onOpenSignedFiles: (act: ArmeniaAct) => void;
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case "DISAGREEMENT":
      return "warning";
    case "SIGNED":
    case "AGREED":
    case "APPROVED":
      return "success";
    case "EXPIRED":
      return "error";
    default:
      return "default";
  }
};

const isActInDisagreement = (act: ArmeniaAct) => {
  return act.status === "DISAGREEMENT";
};

const ArmeniaActsHistoryDrawer = ({
  acts,
  isAdmin,
  onOpenAct,
  onOpenDispute,
  onOpenDisputePreview,
  onUploadSignedAct,
  onAgreeAct,
  onOpenSignedFiles,
}: Props) => {
  const { t } = useTranslation();
  const provider = useSelector((state: RootState) => state.app.provider);
  const hasEnglishOnlyAct = [Provider.UAE, Provider.TFOS].includes(provider);
  const [actLangById, setActLangById] = React.useState<
    Record<number, "ru" | "en">
  >({});

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case "DISAGREEMENT":
        return t("armeniaAct.status.disagreement");
      case "SIGNED":
        return t("armeniaAct.status.signed");
      case "EXPIRED":
        return t("armeniaAct.status.expired");
      case "APPROVED":
        return t("armeniaAct.status.approved");
      case "AGREED":
        return t("armeniaAct.status.agreed");
      case "DRAFT":
        return t("armeniaAct.status.draft");
      default:
        return status || "";
    }
  };

  const columns: GridColDef<ArmeniaAct>[] = [
    {
      field: "version",
      headerName: t("armeniaAct.history.version"),
      width: 90,
      valueGetter: (_, row) => row.version ?? 1,
    },
    {
      field: "act_date",
      headerName: t("armeniaAct.history.actDate"),
      width: 120,
      valueGetter: (_, row) =>
        row.act_date ? dayjs(row.act_date).format("DD.MM.YYYY") : "",
    },
    {
      field: "status",
      headerName: t("armeniaAct.history.status"),
      width: 150,
      renderCell: (params) => (
        <Chip
          size="small"
          label={getStatusLabel(params.row.status)}
          color={getStatusColor(params.row.status) as any}
          variant={params.row.status === "DRAFT" ? "outlined" : "filled"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      minWidth: isAdmin ? 520 : 720,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const act = params.row;
        const hasDispute = isActInDisagreement(act);
        const hasSignedFiles = Number(act.signed_files_count ?? 0) > 0;
        const selectedLang =
          actLangById[act.id] ?? (hasEnglishOnlyAct ? "en" : "ru");

        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            sx={{
              width: "100%",
              minHeight: 64,
              py: 1,
              flexWrap: "wrap",
            }}
          >
            {!hasEnglishOnlyAct && (
              <FormControl size="small" sx={{ minWidth: 78 }}>
                <Select
                  size="small"
                  value={selectedLang}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setActLangById((prev) => ({
                      ...prev,
                      [act.id]: e.target.value as "ru" | "en",
                    }));
                  }}
                >
                  <MenuItem value="ru">RU</MenuItem>
                  <MenuItem value="en">EN</MenuItem>
                </Select>
              </FormControl>
            )}

            <IconButton
              size="small"
              title={t("armeniaAct.history.viewAct")}
              onClick={(e) => {
                e.stopPropagation();
                onOpenAct(act, selectedLang);
              }}
            >
              <VisibilityIcon color="primary" />
            </IconButton>

            {!isAdmin && (
              <>
                {act.status !== "AGREED" && act.status !== "SIGNED" && (
                  <>
                    {act.status !== "DISAGREEMENT" && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDispute(act);
                        }}
                      >
                        {t("armeniaAct.history.dispute")}
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAgreeAct(act);
                      }}
                    >
                      {t("armeniaAct.history.agree")}
                    </Button>
                  </>
                )}

                {(act.status === "AGREED" || act.status === "SIGNED") && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onUploadSignedAct(act);
                    }}
                  >
                    {t("armeniaAct.history.uploadFile")}
                  </Button>
                )}
              </>
            )}

            {hasSignedFiles && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<InsertDriveFileIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenSignedFiles(act);
                }}
              >
                {t("armeniaAct.history.files", {
                  count: act.signed_files_count,
                })}
              </Button>
            )}

            {isAdmin && hasDispute && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<ReportProblemIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDisputePreview(act);
                }}
              >
                {t("armeniaAct.history.disputePreview")}
              </Button>
            )}

            {isAdmin && !hasDispute && !hasSignedFiles && (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 2, maxHeight: "calc(100vh - 120px)", overflow: "auto" }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t("armeniaAct.history.title")}: <strong>{acts.length}</strong>
      </Typography>

      <Box sx={{ height: "calc(100vh - 220px)", minHeight: 260 }}>
        <DataGrid
          rows={acts}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={76}
          hideFooter
          disableRowSelectionOnClick
          sx={{
            "--DataGrid-rowBorderColor": "#D1D3D4",
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              fontSize: 14,
              lineHeight: "110%",
            },
            "& .MuiDataGrid-cell": {
              fontSize: 14,
              borderTop: "0.5px solid #D1D3D4",
              display: "flex",
              alignItems: "center",
              overflow: "visible",
              whiteSpace: "normal",
            },
            "& .MuiDataGrid-row": {
              maxHeight: "none !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              minHeight: "56px !important",
            },
            fontFamily: "inherit",
            borderWidth: 0,
            background: "#ffffff",
          }}
        />
      </Box>
    </Box>
  );
};

export default ArmeniaActsHistoryDrawer;
